var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { retryNon4xx } from "shared/api/query-retry";
import { nftsAdminApi } from "../../api/nftsAdminApi";
var KEYS = {
    byCollection: function (collectionId) {
        return ["admin", "nfts", "collection", collectionId];
    },
    one: function (id) { return ["admin", "nfts", "one", id]; },
};
export var useAdminNfts = function (collectionId) {
    return useQuery({
        queryKey: KEYS.byCollection(collectionId),
        queryFn: function () { return nftsAdminApi.getByCollection(collectionId); },
        enabled: !!collectionId,
        staleTime: 30000,
        retry: retryNon4xx,
    });
};
// Серверная пагинация айтемов (page/limit/status/search + meta). Ключ вложен
// в byCollection-префикс, поэтому существующие инвалидации мутаций его покрывают.
export var useAdminNftsPaged = function (collectionId, params) {
    return useQuery({
        queryKey: __spreadArray(__spreadArray([], KEYS.byCollection(collectionId), true), ["paged", params], false),
        queryFn: function () { return nftsAdminApi.listPaged(collectionId, params); },
        enabled: !!collectionId,
        staleTime: 30000,
        retry: retryNon4xx,
    });
};
export var useAdminNft = function (id) {
    return useQuery({
        queryKey: KEYS.one(id),
        queryFn: function () { return nftsAdminApi.getById(id); },
        enabled: !!id,
        staleTime: 30000,
        retry: retryNon4xx,
    });
};
export var useCreateNft = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var collectionId = _a.collectionId, payload = _a.payload;
            return nftsAdminApi.create(collectionId, payload);
        },
        onSuccess: function (_data, _a) {
            var collectionId = _a.collectionId;
            qc.invalidateQueries({ queryKey: KEYS.byCollection(collectionId) });
            qc.invalidateQueries({ queryKey: ["admin", "collections"] });
        },
    });
};
export var useCreateNftBatch = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var collectionId = _a.collectionId, items = _a.items;
            return nftsAdminApi.createBatch(collectionId, items);
        },
        onSuccess: function (_data, _a) {
            var collectionId = _a.collectionId;
            qc.invalidateQueries({ queryKey: KEYS.byCollection(collectionId) });
            qc.invalidateQueries({ queryKey: ["admin", "collections"] });
        },
    });
};
export var useUpdateNft = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, payload = _a.payload;
            return nftsAdminApi.update(id, payload);
        },
        onSuccess: function (data) {
            qc.invalidateQueries({ queryKey: KEYS.byCollection(data.collectionId) });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
        },
    });
};
export var useDeleteNft = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id;
            return nftsAdminApi.deleteOne(id);
        },
        onSuccess: function (_data, _a) {
            var collectionId = _a.collectionId;
            qc.invalidateQueries({ queryKey: KEYS.byCollection(collectionId) });
            qc.invalidateQueries({ queryKey: ["admin", "collections"] });
        },
    });
};
export var useDelistNft = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id;
            return nftsAdminApi.delist(id);
        },
        onSuccess: function (data, _a) {
            var collectionId = _a.collectionId;
            qc.invalidateQueries({ queryKey: KEYS.byCollection(collectionId) });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
        },
    });
};
export var useResyncNftOwner = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (id) { return nftsAdminApi.resyncOwner(id); },
        onSuccess: function (data) {
            qc.invalidateQueries({ queryKey: KEYS.byCollection(data.collectionId) });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
        },
    });
};
export var useToggleNftFlag = function () {
    var qc = useQueryClient();
    return useMutation({
        mutationFn: function (_a) {
            var id = _a.id, flag = _a.flag;
            return nftsAdminApi.toggleFeatureFlag(id, flag);
        },
        onSuccess: function (data) {
            qc.invalidateQueries({ queryKey: KEYS.byCollection(data.collectionId) });
            qc.invalidateQueries({ queryKey: KEYS.one(data.id) });
        },
    });
};
